import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { canApplyCreditAdjustmentNow } from "utils/permissionUtils";
import { requestCreditAdjustment, useCreditAdjustmentOptions } from "./creditAdjustmentApi";
import {
  defaultCreditMoveAmount,
  overpaidAfterCreditMove,
  validateCreditMove,
} from "./creditAdjustmentUtils";
import { formatAudCompact } from "./termPaymentUtils";

const apiError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const targetLabel = (target) =>
  `T${target.termNumber} · owes ${formatAudCompact(target.due)}${
    target.imported ? "" : " · not imported yet"
  }`;

/** Moves some of an overpaid term's credit onto another term of the same admission. */
export default function CreditAdjustmentDialog({ open, onClose, termId, termLabel, onDone }) {
  const queryClient = useQueryClient();
  const optionsQuery = useCreditAdjustmentOptions(termId, open);
  const options = optionsQuery.data;
  const creditAvailable = Number(options?.creditAvailable) || 0;
  const targets = options?.targets ?? [];
  const mayApplyNow = canApplyCreditAdjustmentNow();

  const [targetTermId, setTargetTermId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [applyNow, setApplyNow] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const target = targets.find((t) => t.termId === targetTermId) ?? null;

  const pickTarget = (next) => {
    setTargetTermId(next?.termId ?? "");
    setAmount(next ? String(defaultCreditMoveAmount(creditAvailable, next.due)) : "");
  };

  useEffect(() => {
    if (!open) return;
    setReason("");
    setApplyNow(false);
    setError(null);
    setConfirming(false);
  }, [open]);

  useEffect(() => {
    if (open && options) pickTarget(options.targets?.[0] ?? null);
    // Re-seed only when fresh options arrive, never while the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options]);

  const close = () => {
    if (!saving) onClose?.();
  };

  const review = () => {
    const problem = validateCreditMove({ creditAvailable, target, amount, reason });
    setError(problem);
    if (!problem) setConfirming(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await requestCreditAdjustment(termId, {
        targetTermId: target.termId,
        amount: Number(amount),
        reason: reason.trim(),
        applyNow: mayApplyNow && applyNow,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admissionTerms"] }),
        queryClient.invalidateQueries({ queryKey: ["pending-payment-verifications"] }),
        queryClient.invalidateQueries({ queryKey: ["credit-adjustment-options"] }),
      ]);
      toast.success(
        mayApplyNow && applyNow ? "Credit moved." : "Credit move sent for verification."
      );
      onDone?.();
      setConfirming(false);
      onClose?.();
    } catch (err) {
      setConfirming(false);
      setError(apiError(err, "Could not move the credit."));
    } finally {
      setSaving(false);
    }
  };

  const overpaidBy = target ? overpaidAfterCreditMove(amount, target.due) : 0;
  const applying = mayApplyNow && applyNow;

  const renderBody = () => {
    if (optionsQuery.isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (optionsQuery.isError) {
      return (
        <Alert severity="error">
          {apiError(optionsQuery.error, "Could not load this term's credit.")}
        </Alert>
      );
    }
    if (creditAvailable <= 0) {
      return <Alert severity="info">{termLabel} has no credit to move right now.</Alert>;
    }
    if (targets.length === 0) {
      return (
        <Alert severity="info">
          No other term of this admission has anything left to pay, so there is nowhere to move the
          credit.
        </Alert>
      );
    }
    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Credit on {termLabel}
          </Typography>
          <Typography variant="h6" fontWeight={600} color="success.main">
            {formatAudCompact(creditAvailable)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Verified paid above the agreed amount, less refunds and moves still waiting on
            verification.
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Move to"
          value={targetTermId}
          onChange={(e) => pickTarget(targets.find((t) => t.termId === e.target.value))}
        >
          {targets.map((t) => (
            <MenuItem key={t.termId} value={t.termId}>
              {targetLabel(t)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="number"
          size="small"
          label="Amount to move (AUD)"
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, max: creditAvailable, step: "0.01" }}
          helperText={`Up to ${formatAudCompact(creditAvailable)}`}
        />
        {overpaidBy > 0 && (
          <Alert severity="info">
            T{target.termNumber} will then be overpaid by {formatAudCompact(overpaidBy)}.
          </Alert>
        )}
        <TextField
          size="small"
          label="Reason"
          required
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why the credit is moving, e.g. student overpaid T1 and asked to use it for T2"
        />
        {mayApplyNow && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={applyNow}
                onChange={(e) => setApplyNow(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Apply now (skip verification)</Typography>}
          />
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    );
  };

  const canReview = !optionsQuery.isLoading && creditAvailable > 0 && targets.length > 0;

  return (
    <>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Move credit from {termLabel}</DialogTitle>
        <DialogContent dividers>{renderBody()}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          {canReview && (
            <Button variant="contained" onClick={review} disabled={saving}>
              Review move
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirming}
        onClose={() => !saving && setConfirming(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Move {formatAudCompact(Number(amount))} from {termLabel} to T{target?.termNumber}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {applying
              ? `It applies straight away: ${termLabel}'s paid amount drops and T${target?.termNumber}'s rises by the same amount.`
              : "It goes to accounts for verification. Neither term changes until it is verified, and rejecting it leaves both as they are."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} disabled={saving}>
            Back
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : applying ? "Move credit now" : "Send for verification"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
